<script lang="ts" setup>
import type { ContentVideo } from '~/models/Content/ContentVideo'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorTheme } from '~/models/Content/Color'
import { useColorTheme } from '~/composables/useColorTheme'
import { useAppColor } from '~/composables/useAppColor'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import ThirdPartyEmbed from '~/components/utils/ThirdPartyEmbed.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import VideoPlayer from '~/components/media/VideoPlayer.vue'
import ArticleNavigation from '~/components/article/ArticleNavigation.vue'

const props = defineProps<{ locationId: number }>()

const { checkItemLicense } = useLicenseControlStore()
const { findContents } = useContentApi()
const { isAuthenticated } = storeToRefs(useAuthStore())
const { style, theme } = useColorTheme(ColorTheme.BlueOrange)
useAppColor(theme.value[10])

const {
  data: content,
  isLoading,
  isError,
} = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['video-view', props.locationId]),
  queryFn: () => findContents<ContentVideo>({
    subtreeCriterion: [Subtree.Media, Subtree.Content],
    locationIdCriterion: [props.locationId],
    contentTypeCriterion: [ContentType.Video],
  }, 1).then(([content]) => content ?? null),
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(content)

watch(() => content.value, () => {
  if (!content.value)
    return
  setTitle(content.value.title)
  checkItemLicense(content.value)
}, { immediate: true })
</script>

<template>
  <NotFound v-if="isError" />
  <div
    v-else
    class="grid min-h-full auto-rows-max grid-rows-[1fr]"
    :style
  >
    <div
      class="font-inter grid grid-cols-[minmax(0,120ch)] grid-rows-[max-content] place-content-center gap-y-6 px-2 py-6"
      :class="{ 'bg-(--theme-10) text-(--theme-60)': isLoading || content }"
    >
      <AuSkeletonWrapper v-if="isLoading">
        <AuSkeleton
          width="33%"
          height="4rem"
        />

        <AuSkeleton
          width="100%"
          height="auto"
          class="aspect-video"
        />
      </AuSkeletonWrapper>

      <template v-else-if="content">
        <TopBar
          :item="content"
          :fullscreen="false"
        />
        <h1
          class="text-5xl font-bold"
          v-text="content.title"
        />

        <ThirdPartyEmbed
          v-if="isAuthenticated && content.embedCode"
          :code="content.embedCode"
        />

        <VideoPlayer
          v-else-if="isAuthenticated && content.metadata"
          :media-id="content.metadata.contentItemId"
          :src="content.metadata.elementURI"
          :poster-src="content.metadata.image"
        />
      </template>
    </div>
    <ArticleNavigation
      v-if="content"
      :item="content"
    />
  </div>
</template>
